import styled from "@emotion/styled";
import type {CSSProperties, ReactNode} from "react";
import {useEffect, useRef} from "react";
import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/scrollbar";
import type SwiperInstance from "swiper";
import {log} from "@/utils/log";

// The horizontal sliders of the site (cards, images, questions). Swiper is ~75 KB of script (23 KB gzip) that no visitor needs before
// they scroll to a slider, and most pages have none (the home page has none): it used to be in the first script of every page, because the
// sections that use it are in a shared chunk. Now:
//  - the server, and the first render of the browser, write the markup that Swiper's own React component wrote (`.swiper` > `.swiper-wrapper` >
//    `.swiper-slide`s, then `.swiper-scrollbar`), so that the cards are in the HTML for search engines and hydration finds what the server sent ;
//  - the script of Swiper is fetched when the slider comes within SWIPER_MARGIN of the screen (at once without IntersectionObserver), and attaches
//    to that markup: the slides, the scrollbar and the free mode behave as before.
// Remount a slider whose options change with a `key` (as `<Swiper key={...}>` was): the options are read once, when Swiper starts.

const SWIPER_MARGIN = "400px";

// Until Swiper starts, the slider is laid out as close as possible to how it ends up, so that nothing moves when it starts:
//  - Swiper gives each slide `spaceBetween` as a right margin: written here too (the width of a slide is the width of its content before and after: the
//    sections' styles say `width: auto`) ;
//  - the scrollbar is out of the flow, as Swiper puts it, and not shown yet (its stylesheet only makes it absolute once `.swiper-horizontal` is there:
//    until then it was a bar of 3 to 8 px under the slides, and the content below moved up when Swiper started).
const Root = styled.div<{gap: number}>(({gap}) => ({
  "&:not(.swiper-initialized)": {
    "> .swiper-wrapper > .swiper-slide": {marginRight: gap},
    "> .swiper-scrollbar": {position: "absolute", visibility: "hidden"},
  },
}));

let swiperModule: Promise<typeof import("./swiper.client")> | undefined;
const loadSwiper = () => {
  swiperModule ??= import("./swiper.client").catch((err) => {
    // a failed download is tried again the next time a slider asks
    swiperModule = undefined;
    throw err;
  });
  return swiperModule;
};

interface SliderProps {
  children: ReactNode;
  // Space between slides, in px
  spaceBetween?: number;
  // The slider keeps its momentum when released (desktop)
  freeMode?: boolean;
  // The bar under the slides
  scrollbar: {draggable: boolean; dragSize: number | "auto"};
  style?: CSSProperties;
}

export const Slider = ({
  children,
  spaceBetween = 0,
  freeMode = false,
  scrollbar,
  style,
}: SliderProps) => {
  const root = useRef<HTMLDivElement>(null);
  const bar = useRef<HTMLDivElement>(null);
  const swiper = useRef<SwiperInstance | null>(null);
  const {draggable, dragSize} = scrollbar;

  useEffect(() => {
    const element = root.current;
    if (!element) return;
    let cancelled = false;

    const start = () => {
      loadSwiper().then(
        ({Swiper, FreeMode, Scrollbar}) => {
          if (cancelled) return;
          swiper.current = new Swiper(element, {
            modules: [FreeMode, Scrollbar],
            slidesPerView: "auto",
            spaceBetween,
            initialSlide: 0,
            freeMode: {enabled: freeMode},
            scrollbar: {el: bar.current, draggable, dragSize, snapOnRelease: false},
          });
        },
        (err) =>
          log.warn(
            {event: "slider.load_failed", err},
            "the script of the slider did not load: the cards stay in place, without scrolling",
          ),
      );
    };

    const observer =
      typeof IntersectionObserver === "undefined"
        ? null
        : new IntersectionObserver(
            (entries) => {
              if (!entries.some((entry) => entry.isIntersecting)) return;
              observer?.disconnect();
              start();
            },
            {rootMargin: SWIPER_MARGIN},
          );
    if (observer) observer.observe(element);
    else start();

    return () => {
      cancelled = true;
      observer?.disconnect();
      // The markup stays: React removes it, or keeps it as it is
      swiper.current?.destroy(true, false);
      swiper.current = null;
    };
  }, [spaceBetween, freeMode, draggable, dragSize]);

  // The slides may change under a running slider (a filter gives other cards): Swiper measures them again
  useEffect(() => {
    swiper.current?.update();
  });

  return (
    <Root ref={root} gap={spaceBetween} className="swiper" style={style}>
      <div className="swiper-wrapper">{children}</div>
      <div ref={bar} className="swiper-scrollbar" />
    </Root>
  );
};

export const Slide = ({
  children,
  style,
}: {
  children: ReactNode;
  style?: CSSProperties;
}) => (
  <div className="swiper-slide" style={style}>
    {children}
  </div>
);
